/**
 * Dashboard filters (screenshots/84 "FILTERS  Since Jan 1, 2025 ⌄  Topic: Housing ⌄  Wards: All ⌄  + Filter", and the
 * coverage note on the right "Applies to all 6 widgets · Whip count ignores Wards", `filterSummary`). Each chip opens a
 * small editor; "+ Filter" opens the list of filters. Editors save the filters on the dashboard; viewers change them for
 * themselves only. On phones (91) they're plain chips ("Since Jan 1, 2025 · Housing · All wards").
 * The editors aren't designed (they're minimal: a date, a topic, ward numbers, and Clear).
 */
import { useEffect, useRef, useState } from "react";
import { CalendarDays, ChevronDown, Info, MapPin, Plus, Tag } from "lucide-react";
import { filterSummary, type DashboardFilterKey } from "../overview/widgets/registry";
import { activeFilters, filterChip, type PageFilters } from "./pageFilters";

const ICON = { since: CalendarDays, topic: Tag, wards: MapPin } as const;
const KEYS: DashboardFilterKey[] = ["since", "topic", "wards"];

export function DashboardFilters({ filters, types, onChange }: { filters: PageFilters; types: string[]; onChange: (f: PageFilters) => void }) {
  const [open, setOpen] = useState<DashboardFilterKey | "add" | null>(null);
  const summary = filterSummary(types, activeFilters(filters));
  return (
    <div className="an-filterbar" role="group" aria-label="Dashboard filters">
      <span className="eyebrow an-filterbar__label">Filters</span>
      {KEYS.map((k) => {
        const I = ICON[k];
        return (
          <span key={k} className="an-filter">
            <button className="an-filter__chip" aria-haspopup="dialog" aria-expanded={open === k} onClick={() => setOpen(open === k ? null : k)}>
              <I size={15} aria-hidden />{filterChip(k, filters)}<ChevronDown size={14} aria-hidden />
            </button>
            {open === k && <Editor k={k} filters={filters} onChange={onChange} onClose={() => setOpen(null)} />}
          </span>
        );
      })}
      <span className="an-filter">
        <button className="an-filter__add" aria-haspopup="menu" aria-expanded={open === "add"} onClick={() => setOpen(open === "add" ? null : "add")}><Plus size={14} aria-hidden />Filter</button>
        {open === "add" && (
          <ul className="widget-menu ov-menu an-menu" role="menu" aria-label="Filter">
            {KEYS.map((k) => { const I = ICON[k]; return <li key={k} role="none"><button role="menuitem" onClick={() => setOpen(k)}><I size={15} aria-hidden />{filterChip(k, {}).split(":")[0]}</button></li>; })}
          </ul>
        )}
      </span>
      {summary && <span className="an-filterbar__note"><Info size={14} aria-hidden />{summary}</span>}
    </div>
  );
}

function Editor({ k, filters, onChange, onClose }: { k: DashboardFilterKey; filters: PageFilters; onChange: (f: PageFilters) => void; onClose: () => void }) {
  const box = useRef<HTMLFormElement>(null);
  const cur = filters[k];
  const [value, setValue] = useState(Array.isArray(cur) ? cur.join(", ") : cur ?? "");
  useEffect(() => {
    box.current?.querySelector("input")?.focus();
    const away = (e: PointerEvent) => { if (!box.current?.contains(e.target as Node) && !(e.target as HTMLElement).closest?.(".an-filter")) onClose(); };
    addEventListener("pointerdown", away, true);
    return () => removeEventListener("pointerdown", away, true);
  }, [onClose]);
  const apply = (v: string) => {
    const next = { ...filters };
    if (!v.trim()) delete next[k];
    else next[k] = k === "wards" ? v.split(/[\s,]+/).filter((w) => /^\d{1,2}$/.test(w) && Number(w) >= 1 && Number(w) <= 50) : v.trim();
    onChange(next);
    onClose();
  };
  const label = filterChip(k, {}).split(":")[0];
  return (
    <form ref={box} className="widget-menu an-menu an-menu--form an-filter__editor" role="dialog" aria-label={label}
      onSubmit={(e) => { e.preventDefault(); apply(value); }} onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } }}>
      <label className="an-field"><span>{label}</span>
        {k === "since" ? <input type="date" value={value} onChange={(e) => setValue(e.target.value)} />
          : <input value={value} inputMode={k === "wards" ? "numeric" : undefined} onChange={(e) => setValue(e.target.value)} />}
      </label>
      <span className="an-menu__btns">
        <button type="button" className="btn btn--secondary" onClick={() => apply("")}>Clear</button>
        <button type="submit" className="btn btn--primary">Apply</button>
      </span>
    </form>
  );
}

/** Phones (91): "Since Jan 1, 2025" · "Housing" · "All wards". */
export function MobileFilterChips({ filters }: { filters: PageFilters }) {
  const ws = Array.isArray(filters.wards) ? filters.wards : [];
  const chips = [filterChip("since", filters), typeof filters.topic === "string" && filters.topic ? filters.topic : "All topics", ws.length ? `Ward${ws.length > 1 ? "s" : ""} ${ws.join(", ")}` : "All wards"];
  return <div className="an-mfilters">{chips.map((c) => <span key={c} className="an-mfilters__chip">{c}</span>)}</div>;
}
